import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { maintenanceApi, type MaintenanceDashboard, type MaintenancePlan } from "../../api/maintenanceApi";
import { dashboardApi } from "../../api/dashboardApi";

const TRIGGER_LABEL: Record<string, string> = {
  BY_DATE: "Ogni N giorni",
  BY_CYCLES: "Ogni N cicli",
  BY_HOURS: "Ogni N ore",
  MANUAL: "Manuale",
};

const PRIORITY_BADGE: Record<string, string> = {
  LOW: "bg-secondary",
  MEDIUM: "bg-info",
  HIGH: "bg-warning",
  CRITICAL: "bg-danger",
};

export default function MaintenancePage() {
  const { token } = useAuth() as { token: string | null };
  const [data, setData] = useState<MaintenanceDashboard | null>(null);
  const [allPlans, setAllPlans] = useState<MaintenancePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    maintenanceApi
      .getDashboard(token)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    maintenanceApi.listPlans(token).then(setAllPlans).catch(() => setAllPlans([]));
  }, [token]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center gap-2 text-muted">
          <div className="spinner-border spinner-border-sm" />
          Caricamento…
        </div>
      </div>
    );
  }

  const alertsCount = data?.alertsCount ?? 0;
  const openWO = data?.openWorkOrders ?? [];
  const dueSoon = data?.plansDueSoon ?? [];

  return (
    <div className="container-fluid py-4">
      <div className="maintenance-hero mb-4">
        <h1>
          <i className="bi bi-wrench-adjustable-circle me-2" />
          Manutenzione
        </h1>
        <p className="maintenance-sub mb-0">
          Piani di manutenzione per macchina, ordini di lavoro e notifiche
        </p>
      </div>

      {/* Banner notifiche lampeggianti */}
      {alertsCount > 0 && (
        <div className="maintenance-alert-banner mb-4">
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-exclamation-triangle-fill maintenance-alert-icon" />
            <div>
              <strong>Attenzione: manutenzione richiesta</strong>
              <div className="small opacity-90">
                {openWO.length} ordini aperti,{" "}
                {dueSoon.filter((d) => {
                  if (d.daysUntilDue != null && d.daysUntilDue <= 0) return true;
                  if (d.cyclesUntilDue != null && d.cyclesUntilDue <= 0) return true;
                  return false;
                }).length}{" "}
                piani in ritardo
              </div>
            </div>
            <span className="maintenance-alert-badge">{alertsCount}</span>
          </div>
        </div>
      )}

      {/* KPI cards - cliccabili per scorrere alla sezione */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <button
            type="button"
            className="card border-0 shadow-sm h-100 w-100 text-start maintenance-kpi-clickable"
            onClick={() => scrollTo("ordini-aperti")}
          >
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="maintenance-kpi-icon bg-primary bg-opacity-10 text-primary">
                    <i className="bi bi-clipboard-check" />
                  </div>
                  <div>
                    <div className="h4 mb-0">{openWO.length}</div>
                    <div className="text-muted small">Ordini aperti</div>
                  </div>
                </div>
                <i className="bi bi-chevron-right text-muted" />
              </div>
            </div>
          </button>
        </div>
        <div className="col-md-4">
          <button
            type="button"
            className="card border-0 shadow-sm h-100 w-100 text-start maintenance-kpi-clickable"
            onClick={() => scrollTo("piani-scadenza")}
          >
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="maintenance-kpi-icon bg-warning bg-opacity-10 text-warning">
                    <i className="bi bi-calendar-event" />
                  </div>
                  <div>
                    <div className="h4 mb-0">
                      {dueSoon.filter((d) => {
                        if (d.daysUntilDue != null && d.daysUntilDue <= 0) return true;
                        if (d.cyclesUntilDue != null && d.cyclesUntilDue <= 0) return true;
                        return false;
                      }).length}
                    </div>
                    <div className="text-muted small">Piani in scadenza</div>
                  </div>
                </div>
                <i className="bi bi-chevron-right text-muted" />
              </div>
            </div>
          </button>
        </div>
        <div className="col-md-4">
          <button
            type="button"
            className="card border-0 shadow-sm h-100 w-100 text-start maintenance-kpi-clickable"
            onClick={() => scrollTo("piani-attivi")}
          >
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div className="maintenance-kpi-icon bg-success bg-opacity-10 text-success">
                    <i className="bi bi-journal-bookmark" />
                  </div>
                  <div>
                    <div className="h4 mb-0">{allPlans.length || (data?.totalPlans ?? 0)}</div>
                    <div className="text-muted small">Piani attivi</div>
                  </div>
                </div>
                <i className="bi bi-chevron-right text-muted" />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Work orders aperti */}
      <div id="ordini-aperti" className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            <i className="bi bi-clipboard2-pulse me-2" />
            Ordini di lavoro aperti
          </h5>
          {openWO.length > 0 && (
            <span className="badge bg-primary">{openWO.length}</span>
          )}
        </div>
        <div className="card-body">
          {openWO.length === 0 ? (
            <p className="text-muted mb-0">Nessun ordine aperto.</p>
          ) : (
            <div className="list-group list-group-flush">
              {openWO.map((wo) => {
                const doneCount = wo.items.filter((i) => i.isDone).length;
                const totalCount = wo.items.length;
                const handleDelete = async () => {
                  if (!window.confirm("Vuoi davvero cancellare questo ordine di lavoro?")) return;
                  try {
                    await maintenanceApi.deleteWorkOrder(token, wo.id);
                    // Ricarica dashboard
                    const dash = await maintenanceApi.getDashboard(token);
                    setData(dash);
                  } catch (e) {
                    alert("Errore durante la cancellazione dell'ordine di lavoro");
                  }
                };
                return (
                  <div
                    key={wo.id}
                    className="list-group-item list-group-item-action d-flex align-items-start gap-3"
                  >
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <strong>{wo.title}</strong>
                        <span className={`badge ${PRIORITY_BADGE[wo.priority] ?? "bg-secondary"}`}>
                          {wo.priority}
                        </span>
                        <span className="text-muted small">{wo.machine?.name}</span>
                      </div>
                      {wo.description && (
                        <p className="small text-muted mb-1">{wo.description}</p>
                      )}
                      {totalCount > 0 && (
                        <div className="small">
                          Checklist: {doneCount}/{totalCount} completate
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className={`badge ${wo.status === "IN_PROGRESS" ? "bg-info" : "bg-warning"}`}
                      >
                        {wo.status === "IN_PROGRESS" ? "In corso" : "Aperto"}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleDelete}
                        title="Elimina ordine di lavoro"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Piani in scadenza */}
      <div id="piani-scadenza">
        {dueSoon.length > 0 ? (
          <div className="card border-0 shadow-sm mb-4 border-warning">
            <div className="card-header bg-warning bg-opacity-10 d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="bi bi-calendar-x me-2" />
                Piani in scadenza
              </h5>
            </div>
            <div className="card-body">
              <div className="list-group list-group-flush">
                {dueSoon.map(({ plan, daysUntilDue, cyclesUntilDue, nextDueAt, severity }) => {
                  const isOverdue =
                    (daysUntilDue != null && daysUntilDue <= 0) ||
                    (cyclesUntilDue != null && cyclesUntilDue <= 0);
                  const badgeClass =
                    severity === "CRITICAL" || isOverdue
                      ? "bg-danger"
                      : severity === "HIGH"
                        ? "bg-warning text-dark"
                        : severity === "MEDIUM"
                          ? "bg-info"
                          : "bg-secondary";
                  const dueLabel =
                    daysUntilDue != null
                      ? isOverdue
                        ? `In ritardo di ${Math.abs(daysUntilDue)} gg`
                        : `Tra ${daysUntilDue} gg`
                      : cyclesUntilDue != null
                        ? isOverdue
                          ? `In ritardo di ${Math.abs(cyclesUntilDue)} cicli`
                          : `Tra ${cyclesUntilDue} cicli`
                        : "";

                  const handleConfirmDone = async () => {
                    if (!window.confirm("Confermi che la manutenzione è stata eseguita?")) return;
                    try {
                      await maintenanceApi.confirmPlanDone(token, plan.id);
                      // ricarica dashboard e piani per riallineare severità e prossime scadenze
                      const dash = await maintenanceApi.getDashboard(token);
                      // ma rimuovi comunque questo piano dall'elenco "in scadenza" corrente
                      const filteredDash = {
                        ...dash,
                        plansDueSoon: dash.plansDueSoon.filter((p) => p.plan.id !== plan.id),
                      };
                      setData(filteredDash);
                      const plans = await maintenanceApi.listPlans(token);
                      setAllPlans(plans);
                    } catch {
                      alert("Errore durante la conferma del piano.");
                    }
                  };

                  return (
                    <div key={plan.id} className="list-group-item">
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <div>
                          <strong>{plan.name}</strong>
                          <span className="text-muted small ms-2">— {plan.machine?.name}</span>
                          <div className="small text-muted mt-1">
                            {nextDueAt ? (
                              <>Scadenza: {new Date(nextDueAt).toLocaleDateString("it-IT")} · </>
                            ) : null}
                            {plan.trigger === "BY_CYCLES" && plan.everyCycles ? (
                              <>Ogni {plan.everyCycles} cicli</>
                            ) : null}
                            {plan.tasks?.length > 0 && (
                              <> · {plan.tasks.length} operazioni dalla documentazione</>
                            )}
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end gap-2">
                          <span className={`badge ${badgeClass}`}>{dueLabel}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            onClick={handleConfirmDone}
                          >
                            <i className="bi bi-check2-circle me-1" />
                            Conferma fatta
                          </button>
                        </div>
                      </div>
                      {plan.tasks && plan.tasks.length > 0 && (
                        <div className="mt-2 ps-3 border-start border-2 border-light">
                          {plan.tasks.slice(0, 3).map((t) => (
                            <div key={t.id} className="small">
                              · {t.title}
                              {t.instructions && (
                                <span className="text-muted"> — {t.instructions.slice(0, 80)}…</span>
                              )}
                            </div>
                          ))}
                          {plan.tasks.length > 3 && (
                            <div className="small text-muted">…e altre {plan.tasks.length - 3}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body text-muted text-center py-3">
              Nessun piano in scadenza.
            </div>
          </div>
        )}
      </div>

      {/* Piani attivi */}
      <div id="piani-attivi" className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-transparent d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            <i className="bi bi-journal-bookmark me-2" />
            Tutti i piani attivi
          </h5>
          <span className="badge bg-success">{allPlans.length}</span>
        </div>
        <div className="card-body">
          {allPlans.length === 0 ? (
            <p className="text-muted mb-0">Nessun piano configurato. Crea un nuovo piano qui sotto.</p>
          ) : (
            <div className="list-group list-group-flush">
              {allPlans.map((plan) => {
                const handleDeletePlan = async () => {
                  if (!window.confirm(`Vuoi davvero cancellare il piano "${plan.name}"? Questa operazione è irreversibile.`)) {
                    return;
                  }
                  setDeletingPlanId(plan.id);
                  try {
                    await maintenanceApi.deletePlan(token, plan.id);
                    // Ricarica i piani
                    const plans = await maintenanceApi.listPlans(token);
                    setAllPlans(plans);
                    // Ricarica dashboard
                    const dash = await maintenanceApi.getDashboard(token);
                    setData(dash);
                  } catch (e) {
                    alert("Errore durante la cancellazione del piano di manutenzione");
                  } finally {
                    setDeletingPlanId(null);
                  }
                };

                return (
                  <div key={plan.id} className="list-group-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="flex-grow-1">
                        <strong>{plan.name}</strong>
                        <span className="text-muted small ms-2">— {plan.machine?.name}</span>
                        {plan.description && (
                          <div className="small text-muted mt-1">{plan.description}</div>
                        )}
                        {plan.tasks && plan.tasks.length > 0 && (
                          <div className="mt-2 ps-3 border-start border-2 border-light">
                            {plan.tasks.map((t) => (
                              <div key={t.id} className="small">
                                · {t.title}
                                {t.instructions && (
                                  <span className="text-muted"> — {t.instructions}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-secondary">{TRIGGER_LABEL[plan.trigger] ?? plan.trigger}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handleDeletePlan}
                          disabled={deletingPlanId === plan.id}
                          title="Elimina piano di manutenzione"
                        >
                          {deletingPlanId === plan.id ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <i className="bi bi-trash" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Form nuovo piano */}
      <CreatePlanForm
        token={token}
        onCreated={() => {
          maintenanceApi.getDashboard(token).then(setData);
        }}
      />

      <p className="text-muted small mt-3">
        Ogni piano può avere task con istruzioni dalla documentazione del costruttore.
        Il cron genera automaticamente ordini di lavoro quando scade il periodo (es. ogni N giorni).
      </p>
    </div>
  );
}

function CreatePlanForm({
  token,
  onCreated,
}: {
  token: string | null;
  onCreated: () => void;
}) {
  const [show, setShow] = useState(false);
  const [machines, setMachines] = useState<{ id: string; name: string; provider?: string }[]>([]);
  const [name, setName] = useState("");
  const [machineId, setMachineId] = useState("");
  const [everyDays, setEveryDays] = useState(30);
  const [description, setDescription] = useState("");
  const [tasks, setTasks] = useState<{ title: string; instructions: string }[]>([
    { title: "", instructions: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      Promise.all([
        dashboardApi.getDashboard(token).catch(() => null),
        dashboardApi.getElettrobarDashboard(token).catch(() => null),
      ])
        .then(([dash, elettro]: any[]) => {
          const items = dash?.machinesDashboard?.items ?? dash?.items ?? [];

          const mainMachines: { id: string; name: string; provider?: string }[] = items.map(
            (m: any) => ({
              id: m.id,
              name: m.name || m.id,
              provider: m.provider,
            }),
          );

          const elettroMachines: { id: string; name: string; provider?: string }[] =
            elettro?.machines?.map((m: any) => ({
              id: m.id,
              name: m.name || m.id,
              provider: m.provider || "ELETTROBAR",
            })) ?? [];

          const seen = new Set<string>();
          const combined = [...mainMachines, ...elettroMachines];
          const list = combined.filter((m) => m.id && !seen.has(m.id) && (seen.add(m.id), true));

          setMachines(list);
          if (list.length && !machineId) setMachineId(list[0]?.id ?? "");
        })
        .catch(() => setMachines([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- machineId intentionally omitted: only set initial value when form opens
  }, [show, token]);

  const addTask = () => setTasks((t) => [...t, { title: "", instructions: "" }]);
  const removeTask = (i: number) => setTasks((t) => t.filter((_, idx) => idx !== i));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !machineId) {
      setError("Nome e macchina obbligatori");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const plan = await maintenanceApi.createPlan(
        token,
        {
          machineId,
          name: name.trim(),
          trigger: "BY_DATE",
          everyDays,
          priority: "MEDIUM",
          ...(description.trim() ? { description: description.trim() } : {}),
        },
      );
      for (let i = 0; i < tasks.length; i++) {
        const t = tasks[i];
        if (t && t.title.trim()) {
          await maintenanceApi.createTask(
            token,
            plan.id,
            {
              title: t.title.trim(),
              orderIndex: i,
              ...(t.instructions.trim() ? { instructions: t.instructions.trim() } : {}),
            },
          );
        }
      }
      setName("");
      setDescription("");
      setTasks([{ title: "", instructions: "" }]);
      setEveryDays(30);
      setShow(false);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore creazione piano");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-4">
      <button
        type="button"
        className="btn btn-outline-primary"
        onClick={() => setShow(!show)}
      >
        <i className={`bi bi-${show ? "dash" : "plus"}-circle me-2`} />
        {show ? "Chiudi" : "Nuovo piano di manutenzione"}
      </button>
      {show && (
        <form onSubmit={handleSubmit} className="card-body border-top mt-2">
          {error && <div className="alert alert-danger small">{error}</div>}
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small">Macchina</label>
              <select
                className="form-select form-select-sm"
                value={machineId}
                onChange={(e) => setMachineId(e.target.value)}
                required
              >
                <option value="">Seleziona…</option>
                {machines.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.provider ? ` (${m.provider})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label small">Nome piano</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="es. Manutenzione mensile"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label small">Ogni N giorni</label>
              <input
                type="number"
                className="form-control form-control-sm"
                min={1}
                value={everyDays}
                onChange={(e) => setEveryDays(Number(e.target.value) || 30)}
              />
            </div>
            <div className="col-12">
              <label className="form-label small">Descrizione</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Opzionale"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
          <div className="mb-2">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label small mb-0">Task (istruzioni dalla documentazione)</label>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addTask}>
                <i className="bi bi-plus" /> Aggiungi
              </button>
            </div>
            {tasks.map((t, i) => (
              <div key={i} className="d-flex gap-2 mb-2 align-items-start">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Titolo operazione"
                  value={t.title}
                  onChange={(e) =>
                    setTasks((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, title: e.target.value } : p))
                    )
                  }
                />
                <input
                  type="text"
                  className="form-control form-control-sm flex-grow-1"
                  placeholder="Istruzioni (da documentazione)"
                  value={t.instructions}
                  onChange={(e) =>
                    setTasks((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, instructions: e.target.value } : p))
                    )
                  }
                />
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeTask(i)}
                  disabled={tasks.length <= 1}
                >
                  <i className="bi bi-trash" />
                </button>
              </div>
            ))}
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" />
                Salvataggio…
              </>
            ) : (
              <>Crea piano</>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
